import React, { useEffect, useMemo, useReducer, useState } from "react";
import {
  ExternalMatter,
  ExternalMatterData,
  ExternalMatterStatus,
  Matter,
  OptionData,
} from "../../data-types";
import { Modal, Spin, Tooltip, Typography } from "antd";
import { ExternalMatterAutoCompleteDropdown } from "./external-matter-auto-complete-dropdown";
import { Spinner } from "../spinner";
import styles from "./external-matter-selector.module.css";
import { InfiniteScrollSelect } from "../infinite-scroll-select";
import { LinkOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import _ from "lodash";

const {
  ["external-matter-selector"]: externalMatterSelectorWrapper,
  ["dropdown-no-options"]: dropdownNoOptionsClassName,
  ["disabled"]: disabledClassName,
} = styles as any;

type InternalMatterExternalMatterUpdate = {
  externalMatterId: number;
};

declare type ExternalMatterSelectorProps = {
  clientId: number | null;
  defaultValue?: number | null;
  disabled: boolean;
  externalMatterData?: ExternalMatterData;
  matter?: Matter;
  onMatterExternalMatterUpdate: (
    matter: InternalMatterExternalMatterUpdate
  ) => Promise<void> | void;
  pmsName: string;
  useExternalMatter: boolean;
  selectedCurrencyCode?: string;
  isEditMode?: boolean;
};

declare type ExternalMatterOption = {
  id: number;
  label: JSX.Element;
  title: string | null;
  externalReference: string | null;
  value: number;
  disabled?: boolean;
};

export const ExternalMatterSelector: React.FC<ExternalMatterSelectorProps> = ({
  clientId,
  defaultValue: defaultValueRaw,
  disabled,
  externalMatterData,
  onMatterExternalMatterUpdate,
  pmsName,
  useExternalMatter,
  matter,
  selectedCurrencyCode,
  isEditMode,
}) => {
  const [modal, contextHolder] = Modal.useModal();
  const externalMatters = externalMatterData?.data;

  const joinedItems =
    matter?.externalMatter &&
    externalMatters?.items.map((x) => x.id).includes(matter?.externalMatter.id)
      ? externalMatters?.items ?? []
      : ([...(externalMatters?.items ?? []), matter?.externalMatter ?? []] as ExternalMatter[]);

  const filteredExternalMatters = {
    items: joinedItems,
    total: externalMatters?.total ?? 0,
  };

  const defaultValue = defaultValueRaw || matter?.externalMatterId;

  const [selectedValue, setSelectedValue] = useState<number | null>(defaultValue ?? null);

  useEffect(() => {
    setSelectedValue(defaultValue ?? null);
  }, [defaultValue]);

  const [loading, setLoading] = useState<boolean>(false);

  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    if (!useExternalMatter) {
      setSelectedValue(null);
    }
  }, [useExternalMatter]);

  useEffect(() => {
    externalMatterData?.get({
      clientId,
      statuses: [ExternalMatterStatus.Unassigned, ExternalMatterStatus.Assigned],
    });
  }, [clientId]);

  const NoOptions = (
    <div className={dropdownNoOptionsClassName}>
      <div>
        <h2>No {pmsName} Matters available</h2>
        <h4>
          Try altering your search terms, or making sure that {pmsName} Matters exist for this
          Client.
        </h4>
      </div>
    </div>
  );

  const [showChangeConfirmation, setShowChangeConfirmation] = useState<{
    show: boolean;
    selectedValue: number | null;
  }>({ show: false, selectedValue: null });

  const [displayState, displayDispatch] = useReducer(displayReducer, {
    searchOpen: false,
  });

  const externalMatterOptions = useMemo<ExternalMatterOption[]>(
    () =>
      renderExternalMatterOptions(filteredExternalMatters?.items ?? [], selectedValue, searchValue),
    [externalMatters]
  );

  const doUpdate = async (newValue: number) => {
    setLoading(true);
    try {
      const externalMatterCurrencyCode = filteredExternalMatters?.items.find(
        (x) => x.id == newValue
      )?.currency?.code;
      if (
        isEditMode &&
        selectedCurrencyCode &&
        externalMatterCurrencyCode &&
        selectedCurrencyCode !== externalMatterCurrencyCode
      ) {
        modal.warning({
          title: "Currency Mismatch",
          content: (
            <div>
              <p>The selected {pmsName} Matter has a different currency to the current Proposal.</p>
              <p>
                Please change the currency of the Proposal to {externalMatterCurrencyCode} to match
                the {pmsName} Matter.
              </p>
            </div>
          ),
        });
        return;
      }

      const targetMAtter: InternalMatterExternalMatterUpdate = {
        externalMatterId: newValue,
      };

      await onMatterExternalMatterUpdate(targetMAtter);
      setSelectedValue(newValue);
    } finally {
      setLoading(false);
      showChangeConfirmation && setShowChangeConfirmation({ show: false, selectedValue });
    }
  };

  const onSearch = useDebouncedCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setSearchValue("");
      setSelectedValue(null);
      externalMatterData?.get({
        clientId,
        statuses: [ExternalMatterStatus.Unassigned, ExternalMatterStatus.Assigned],
      });
      return;
    }
    setSearchValue(value);
    externalMatterData?.get({
      clientId,
      statuses: [ExternalMatterStatus.Unassigned, ExternalMatterStatus.Assigned],
      search: value,
    });
  }, 300);

  const doOnLoadMore =
    (externalMatterData?.data?.items?.length || 0) >= (externalMatterData?.data?.total ?? 0)
      ? undefined
      : () => {
          externalMatterData?.fetchMore({
            clientId,
            statuses: [ExternalMatterStatus.Unassigned, ExternalMatterStatus.Assigned],
            search: searchValue,
          });
        };

  return (
    <div className={externalMatterSelectorWrapper}>
      {contextHolder}
      <Spinner show={loading === true} message={`Updating ${pmsName} Matter...`}>
        <InfiniteScrollSelect
          disabled={disabled}
          showSearch={true}
          optionLabelProp="selectedString"
          onLoadMore={doOnLoadMore}
          allowClear={true}
          onClear={() => setSelectedValue(null)}
          autoClearSearchValue={true}
          notFoundContent={externalMatterData?.loading ?? false ? <Spin /> : NoOptions}
          loading={externalMatterData?.loading || loading || false}
          filterOption={false}
          onChange={async (value, rawOption) => {
            const option = rawOption as OptionData;
            // Only show modal/prompt if the user is changing an External Matter while keeping the same Client
            if (!defaultValue) {
              await doUpdate(value);
            } else if (option?.id !== defaultValue) {
              setShowChangeConfirmation((curr) => ({ ...curr, show: true, selectedValue: value }));
            } else {
              setSelectedValue(value);
            }
          }}
          open={displayState.searchOpen}
          onDropdownVisibleChange={(visible) =>
            displayDispatch({ type: visible ? "open-search" : "close-search" })
          }
          onSearch={(value) => {
            onSearch(value);
          }}
          value={useExternalMatter ? selectedValue : undefined}
          // filterOption={false}
          placeholder={`Enter any part of ${
            /^[aeiou]/i.test(pmsName) ? "an" : "a"
          } ${pmsName} Matter's title`}
          dropdownRender={(menu) => {
            return (
              <ExternalMatterAutoCompleteDropdown
                externalMatterCount={filteredExternalMatters?.total ?? 0}
                pmsName={pmsName}
              >
                {menu}
              </ExternalMatterAutoCompleteDropdown>
            );
          }}
          options={externalMatterOptions}
        />
      </Spinner>
      <Modal
        title={`Change ${pmsName} Matter`}
        centered
        open={showChangeConfirmation.show && showChangeConfirmation.selectedValue !== null}
        onOk={async () => {
          await doUpdate(showChangeConfirmation.selectedValue!);
        }}
        onCancel={() => {
          setShowChangeConfirmation((curr) => ({ ...curr, show: false }));
        }}
      >
        <p>Are you sure you want to change the linked {pmsName} Matter for this Matter?</p>
      </Modal>
    </div>
  );
};

function displayReducer(
  state: {
    searchOpen: boolean;
  },
  action: {
    type: "open-search" | "close-search";
  }
) {
  switch (action.type) {
    case "open-search":
      return {
        searchOpen: true,
      };
    case "close-search":
      return {
        searchOpen: false,
      };
    default:
      return state;
  }
}

function renderExternalMatterOptions(
  externalMatters: ExternalMatter[],
  assignedExternalMatterId: number | null,
  searchValue: string = ""
): ExternalMatterOption[] {
  return externalMatters.map((externalMatter) => {
    const { id, title, externalReference, matter } = externalMatter;
    const assigned = externalMatter.status === ExternalMatterStatus.Assigned;

    const labelContent = externalReference ? `${title} - ${externalReference}` : title;

    const disabled = assigned && assignedExternalMatterId !== id;

    const selectContent = getHighlightedMatch(searchValue, labelContent, disabled);

    return {
      id,
      title,
      externalReference,
      value: id,
      disabled,
      selectedString: labelContent,
      label: (
        <Typography.Text ellipsis>
          <span className={disabled && disabledClassName}>
            {disabled && matter?.id ? (
              <Tooltip
                title={
                  <span>
                    Matter is already link to a
                    <Link
                      style={{ display: "inline" }}
                      target="_blank"
                      to={`/matter-editor/${matter.id}/clientmatterdetails`}
                    >
                      {" "}
                      Proposal
                    </Link>
                  </span>
                }
              >
                {selectContent}
                <LinkOutlined style={{ marginLeft: 5 }} />
              </Tooltip>
            ) : (
              selectContent
            )}
          </span>
        </Typography.Text>
      ),
    };
  });
}

const getHighlightedMatch = (search: string, text: string, disabled: boolean) => {
  if (!text?.trim() || !search?.trim()) {
    return <span>{text}</span>;
  }

  const regex = new RegExp(`(${_.escapeRegExp(search.trim())})`, "gi");
  const parts = text.split(regex);

  return (
    <span>
      {parts
        .filter((part) => part)
        .map((part, i) =>
          regex.test(part) ? (
            <span style={{ color: disabled ? "#ffcccb" : "red" }} key={i}>
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
    </span>
  );
};
